<template>
    <div class="fixed top-0 left-0 z-20 w-full h-full overflow-y-auto bg-white outline-none" id="form-step-4">
        <div class="container flex items-center justify-center h-screen mx-auto">
            <div class="p-8 mx-4 border-2 rounded-lg shadow-md md:mx-auto sm:w-75 md:w-50">
                <div class="text-center">
                    <img :src="logoSrc" alt="Unbundled Legal Help" class="mx-auto img-responsive">
                </div>

                <div class="flex flex-row items-center justify-between pt-8">
                    <div>
                        <i class="fas fa-sync fa-spin fa-3x"></i>
                    </div>

                    <div class="pl-8">
                        <h3 class="text-xl md:text-2xl">
                            Dear {{ form.first_name }},
                        </h3>

                        <span class="text-sm md:text-base lg:text-lg">
                            Just a moment while we connect you with {{ sectorWithArticle }} attorney that serves {{ location }}&#8230;
                        </span>

                        <p v-if="link" class="text-sm md:text-base lg:text-lg mt-2">If not automatically redirected, <a class="text-brand-blue underline hover:text-brand-blue-dark" :href="link">click here</a> to view your provider details.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
const REDIRECT_TIMEOUT = 4000;

export default {
    data () {
        return {
            'link': null,
        }
    },

    computed: {
        ...mapGetters('lead', [
            'form',
            'logoSrc',
            'countyName',
            'pageLocation',
            'selectedLocation',
            'countyName',
        ]),

        sectorWithArticle () {
            return window.SHARED.sector_with_article;
        },

        location () {
            return window.SHARED.location;
        },
    },

    beforeMount() {
        this.$store.commit('meta/disablePopup');
    },

    beforeDestroy() {
        this.$store.commit('meta/enablePopup');
    },

    mounted() {
        // BEFORE this.submit(), order matters.
        window.dataLayer.push({
            event: 'form_step_mounted',
            form_step_id: window.getCurrentStep(),
            form_step_name: 'conversion'
        });

        this.submit();
    },

    methods: {
        /**
         * Submit lead's data and redirect to appropriate url.
         */
        async submit() {
            this.submitting = true;

            window.dataLayer.push({
                event: 'form_step_submit',
                form_step_id: window.getCurrentStep(),
                form_step_name: 'conversion'
            });

            // AFTER form_step_submit, order matters.
            window.dataLayer.push({
                event: 'form_submit',
                form_id: 'ulh-lead-intake',
                form_name: 'ULH Lead Intake'
            });

            axios.post('/requests/post', {
                country: this.country,
                region: this.form.region_id,
                city: this.form.city,
                postcode: this.form.postcode,
                issue: this.form.issue,
                first_name: this.form.first_name,
                last_name:  this.form.last_name,
                language_bcp47:  this.form.language_bcp47 ?? window.navigator.language,
                phone: this.form.phone,
                phone_alt: this.form.phone_alt,
                email: this.form.email,
                overriding_question: this.form.overriding_question,
                overridden_division_slug: this.form.overridden_division_slug,
                metadata: {
                    selected_location: `${this.selectedLocation.City}, ${this.selectedLocation.State}`,
                    sms_opted_in: this.form.sms_opted_in,
                    sms_able: true,
                    phone_type: this.form.phone_type,
                    page_location: this.pageLocation,
                    ga_client_id: window.SHARED.gaClientId,
                    ga_session_id: window.SHARED.gaSessionId
                }
            }).then(({ data }) => {
                this.pushDataLayerEvents(data.lead, data.redirect_url);
                this.clearSavedFormFields();

                // fallback redirect in case the conversion event doesn't fire
                this.addMetaRefreshHeader(data.redirect_url);

                setTimeout(() => {
                    this.redirect(data.redirect_url);
                    this.link = data.redirect_url;
                }, REDIRECT_TIMEOUT + (REDIRECT_TIMEOUT * 0.1));
            }).catch((exception) => {
                if (exception?.response?.status != 422) {
                    if (window.bugsnagClient) {
                        window.bugsnagClient.notify(exception);
                    }
                }

                if (exception?.response?.status === 400) {
                    this.$set(this.form.errors, 'errors', { error: 'Oops, something went wrong on our end. Please try again later.', data: exception?.response?.data });
                } else if (exception?.response?.data?.errors !== undefined) {
                    if(exception.response.data.errors) {
                        this.$set(this.form.errors, 'errors', exception.response.data.errors);
                    }

                    if (this.form.errors.onlyNonDisplayableErrors()) {
                        // Errors here likely mean there is a programmatic issue on the frontend
                        this.$set(this.form.errors, 'errors', { error: 'Oops, something went wrong on our end. Please try again later.', data: exception.response.data });
                    }
                } else {
                    // Errors returned from PostLead::handle
                    this.$set(this.form.errors, 'errors', exception.response.data);
                }

                this.submitting = false;

                if (this.form.errors.onlyDisplayableErrors()
                    && (this.form.errors.has('issue') || this.form.errors.has('overridden_division_slug'))) {
                    this.$event.$emit('step:specific', 2);
                    return;
                }

                this.$event.$emit('step:previous');
            });
        },

        /**
         * @param {object} lead
         * @param {string} redirectUrl
         * @returns {void}
         */
        pushDataLayerEvents (lead, redirectUrl) {
            const eventName = lead.should_report_purchase
                ? 'purchase'
                : 'generate_lead';

            window.dataLayer.push({
                user_id: lead.account_id,
                ga_client_id: window.SHARED.gaClientId,
                ga_session_id: window.SHARED.gaSessionId,
                lead_id: lead.id,
                lead_type: lead.type,
                provider_id: lead.tenant.id,
                provider_name: lead.tenant.name_formatted,
                division_id: lead.division.id,
                division_name: lead.division.name,
                sector_id: lead.sector.id,
                sector_name: lead.sector.name,
                region_id: lead.region.id,
                region_name: lead.region.name,
                ecommerce: null // Populated via GTM
            });
            window.dataLayer.push({
                event: eventName,
                ecommerce: {
                    transaction_id: lead.key,
                    value: lead.cost_formatted,
                    tax: 0,
                    shipping: 0,
                    currency: 'USD',
                    items: [
                    {
                        item_id: `ULH-${lead.sku}`,
                        item_name: `ULH ${lead.division.name} ${lead.region.country.alpha2}`,
                        affiliation: 'Unbundled Attorney',
                        item_brand: 'ULH',
                        item_category: 'Leads',
                        item_category2: lead.division.name,
                        item_category3: lead.sector.name,
                        item_category4: `${lead.region.name}, ${lead.region.state.abbreviation} ${lead.region.country.alpha2}`,
                        item_category5: lead.tenant.id ? `Provider ${lead.tenant.id}` : null,
                        item_variant: `${lead.type_name} ${lead.id}`,
                        price: lead.cost_formatted,
                        quantity: 1
                    }]
                },
                eventTimeout: REDIRECT_TIMEOUT,
                eventCallback: () => {
                    this.redirect(redirectUrl);
                },
            });
        },

        /**
         * @param {string} redirectUrl
         * @returns {void}
         */
        addMetaRefreshHeader (redirectUrl) {
            let meta = document.createElement('meta');
            meta.setAttribute('http-equiv', 'refresh');
            // REDIRECT_TIMEOUT is in ms, this meta tag expects seconds
            meta.setAttribute('content', `${REDIRECT_TIMEOUT / 1000};url=${redirectUrl}`);
            document.getElementsByTagName('head')[0].appendChild(meta);
        },

        /**
         * @param {string} redirectUrl
         * @returns {void}
         */
        redirect (redirectUrl) {
            if (! this.shouldRedirectAfterSubmission()) {
                return;
            }

            window.setTimeout(() => {
                window.location.href = redirectUrl;
            }, 100);
        },

        /**
         * @returns {boolean}
         */
        shouldRedirectAfterSubmission () {
            return ! (window.autoRedirectAfterSubmission === false);
        },

        /**
         * @returns {void}
         */
        clearSavedFormFields () {
            window.localStorage.removeItem('lcsForm');
        }
    }
};
</script>
